import { Component, Host, h, Prop, State, Element } from '@stencil/core';
import { getCurrentMediaQuery } from '../../../utils/getCurrentMediaQuery';

@Component({
    tag: 'sew-ws-table',
    styleUrl: 'sew-ws-table.scss',
    shadow: false,
})
export class SewWsTable {
    @Element() host: HTMLSewWsTableElement;

    @Prop() text: string;
    @Prop() labelShow: string;
    @Prop() labelHide: string;
    @Prop() limitShow: string;
    @Prop() limitHide: string;
    @Prop() limitRows: number;
    @Prop({ reflect: true }) noMargin: boolean;
    @Prop({ reflect: true }) zebra = false;
    @Prop({ reflect: true }) sticky = false;

    @State() tableExpanded = false;
    @State() rowsLimited = true;

    private toggleTable = () => {
        this.tableExpanded = !this.tableExpanded;
    };

    public componentDidRender(): void {
        this.setColumnClasses();

        // check if slot content has changed. It's needed for generated content
        const targetNode = this.host.querySelector('table');
        const observer = new MutationObserver(this.setColumnClasses);
        observer.observe(targetNode, { childList: true, subtree: true });
    }

    componentDidLoad() {
        this.handleRowLimit();
    }

    private setColumnClasses = () => {
        const tableElement = this.host.querySelector('table');
        const tableRight = tableElement.getBoundingClientRect().right;
        const tableLeft = tableElement.getBoundingClientRect().left;
        const tdElements = this.host.querySelectorAll('td, th');

        tdElements.forEach(el => {
            // if right edge is NEARLY the same, the might be some rounding inaccuracies
            if (Math.abs(el.getBoundingClientRect().right - tableRight) < 5) {
                el.classList.add('last-column');
            } else {
                el.classList.remove('last-column');
            }

            if (Math.abs(el.getBoundingClientRect().left - tableLeft) < 5) {
                el.classList.add('first-column');
            } else {
                el.classList.remove('first-column');
            }
        });
    };

    private hideRows = () => {
        const rows = this.host.querySelectorAll('tr');

        rows.forEach((row, index) => {
            if (index === this.limitRows - 1) {
                row.classList.add('last-of-type');
            } else if (index > this.limitRows - 1) {
                row.classList.add('hidden-row');
            }
        });
    };

    private showRows = () => {
        const rows = this.host.querySelectorAll('tr');

        rows.forEach(row => {
            row.classList.remove('hidden-row', 'last-of-type');
        });
    };

    private handleRowLimit = () => {
        const rows = this.host.querySelectorAll('tr');

        // early exit if no limit is set or not needed
        if (!this.limitRows || this.limitRows >= rows.length || getCurrentMediaQuery() === 1) return;

        // hide rows exceeding the limit
        this.hideRows();
    };

    private toggleRowLimit = () => {
        this.rowsLimited = !this.rowsLimited;

        if (this.rowsLimited) {
            this.hideRows();
        } else {
            this.showRows();
        }
    };

    render() {
        return (
            <Host class={{ expanded: this.tableExpanded }}>
                <p>{this.text}</p>

                {!this.tableExpanded && (
                    <button class="open-button" onClick={this.toggleTable}>
                        <sew-icon
                            key="table"
                            class="table-svg"
                            icon="table"
                            size="xl"
                            width="90"
                            height="90"
                        />
                        <span>{this.labelShow}</span>
                        <sew-icon key="down" class="arrow-svg" icon="arrow-down" size="l" />
                    </button>
                )}

                {this.tableExpanded && (
                    <button class="close-button" onClick={this.toggleTable}>
                        <span>{this.labelHide}</span>
                        <sew-icon key="close" class="close-svg" icon="close" />
                    </button>
                )}
                <div class="sew-ws-table-wrapper">
                    <slot></slot>
                </div>
                {this.limitRows && getCurrentMediaQuery() > 1 && (
                    <div class="limit-rows">
                        <button
                            class={{ 'expand-button': true, 'rows-unlimited': !this.rowsLimited }}
                            onClick={this.toggleRowLimit}
                        >
                            {this.rowsLimited && <span>{this.limitShow}</span>}
                            {!this.rowsLimited && <span>{this.limitHide}</span>}
                            <sew-icon class="svg" icon="arrow-chevron-down"></sew-icon>
                        </button>
                    </div>
                )}
            </Host>
        );
    }
}
